import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../services/api"
import { FieldRow, FormPage } from "../../../components/FormPage"
import { DeprecatedDevicePost } from "../../../models/DeprecatedDevice"
import { ControllerRendererProps } from "../../../types/reactHookForm"

const validationSchema = z.object({
  deviceSerialNumber: z.string().min(1, "Device Serial Number is required"),
  certificateSerialNumber: z.string().min(1, "Certificate Serial Number is required"),
})

export function DeprecatedDeviceCreate() {
  const navigate = useNavigate()
  const returnURL = `/deprecated-devices`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<DeprecatedDevicePost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      deviceSerialNumber: "",
      certificateSerialNumber: "",
    })
  }, [reset])

  const onSubmit = useCallback(
    async (values: DeprecatedDevicePost) => {
      try {
        const response = await digitalManagerApi.post(`/deprecatedDevices`, {
          deviceSerialNumber: values.deviceSerialNumber,
          certificateSerialNumber: values.certificateSerialNumber,
        })
        if (response.status === StatusCodes.CREATED) {
          toast.success("Deprecated device created")
          navigate(returnURL)
        }
      } catch {
        toast.error("Cannot create deprecated device")
      }
    },
    [navigate, returnURL]
  )

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  const handleTextChange = useCallback(
    (
      field: ControllerRenderProps<
        DeprecatedDevicePost,
        "deviceSerialNumber" | "certificateSerialNumber"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldDeviceSerialNumber = useCallback(
    ({
      field,
    }: ControllerRendererProps<DeprecatedDevicePost, "deviceSerialNumber">) => (
      <MUITextField
        id="deviceSerialNumber"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.deviceSerialNumber?.message === "string"
            ? { deviceSerialNumber: { message: errors.deviceSerialNumber.message } }
            : undefined
        }
      />
    ),
    [errors.deviceSerialNumber, handleTextChange]
  )

  const renderTextFieldCertificateSerialNumber = useCallback(
    ({
      field,
    }: ControllerRendererProps<DeprecatedDevicePost, "certificateSerialNumber">) => (
      <MUITextField
        id="certificateSerialNumber"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.certificateSerialNumber?.message === "string"
            ? {
                certificateSerialNumber: {
                  message: errors.certificateSerialNumber.message,
                },
              }
            : undefined
        }
      />
    ),
    [errors.certificateSerialNumber, handleTextChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Device Serial Number">
        <Controller
          name="deviceSerialNumber"
          control={control}
          render={renderTextFieldDeviceSerialNumber}
        />
      </FieldRow>
      <FieldRow label="Certificate Serial Number">
        <Controller
          name="certificateSerialNumber"
          control={control}
          render={renderTextFieldCertificateSerialNumber}
        />
      </FieldRow>
    </FormPage>
  )
}
